import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface PushNotificationState {
  isSupported: boolean;
  isSubscribed: boolean;
  permission: NotificationPermission;
  loading: boolean;
  isIOSPWA: boolean;
}

export function usePushNotifications() {
  const { user } = useAuth();
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    isSubscribed: false,
    permission: 'default',
    loading: true,
    isIOSPWA: false,
  });

  // Check if running on iOS PWA
  const checkIsIOSPWA = useCallback(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         (window.navigator as any).standalone === true;
    return isIOS && isStandalone;
  }, []);

  // Check if push notifications are supported
  const checkSupport = useCallback(() => {
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotification = 'Notification' in window;
    
    // On iOS, push is only supported in PWA mode (iOS 16.4+)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                           (window.navigator as any).standalone === true;
      return hasServiceWorker && hasPushManager && hasNotification && isStandalone;
    }
    
    return hasServiceWorker && hasPushManager && hasNotification;
  }, []);

  // Get existing subscription status
  const checkSubscription = useCallback(async () => {
    if (!user || !checkSupport()) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Check if subscription exists in database
        const { data } = await supabase
          .from('push_subscriptions')
          .select('id')
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint)
          .single();
        
        setState(prev => ({
          ...prev,
          isSubscribed: !!data,
          loading: false,
        }));
      } else {
        setState(prev => ({ ...prev, isSubscribed: false, loading: false }));
      }
    } catch (error) {
      console.error('Error checking push subscription:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [user, checkSupport]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!user) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para ativar notificações",
        variant: "destructive",
      });
      return false;
    }

    if (!checkSupport()) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        toast({
          title: "iOS Requerido",
          description: "No iOS, adicione o app à tela inicial para receber notificações",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Não Suportado",
          description: "Seu navegador não suporta notificações push",
          variant: "destructive",
        });
      }
      return false;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      setState(prev => ({ ...prev, permission }));

      if (permission !== 'granted') {
        toast({
          title: "Permissão Negada",
          description: "Você precisa permitir notificações nas configurações do navegador",
          variant: "destructive",
        });
        setState(prev => ({ ...prev, loading: false }));
        return false;
      }

      // Get VAPID public key from server
      const { data: vapidData, error: vapidError } = await supabase.functions.invoke('get-vapid-public-key');
      
      if (vapidError || !vapidData?.publicKey) {
        throw new Error('Failed to get VAPID public key');
      }

      // Convert VAPID key to Uint8Array
      const vapidPublicKey = urlBase64ToUint8Array(vapidData.publicKey);

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      });

      // Extract keys from subscription
      const subscriptionJson = subscription.toJSON();
      const p256dh = subscriptionJson.keys?.p256dh;
      const auth = subscriptionJson.keys?.auth;

      if (!p256dh || !auth) {
        throw new Error('Failed to get subscription keys');
      }

      // Detect device type
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      const deviceType = isIOS ? 'ios' : isAndroid ? 'android' : 'web';

      // Save subscription to database
      const { error: dbError } = await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: subscription.endpoint,
          p256dh,
          auth,
          device_type: deviceType,
        }, {
          onConflict: 'user_id,endpoint',
        });

      if (dbError) {
        throw dbError;
      }

      setState(prev => ({ ...prev, isSubscribed: true, loading: false }));
      
      toast({
        title: "Notificações Ativadas",
        description: "Você receberá lembretes de cuidados com suas plantas",
      });

      // Send welcome notification
      try {
        const { data: pushResult, error: pushError } = await supabase.functions.invoke('send-push-notification', {
          body: {
            userId: user.id,
            title: '🌱 VerdeIA',
            body: 'Notificações ativadas! Assim você receberá lembretes para cuidar das suas plantas.',
            data: { type: 'welcome' },
          },
        });
        
        console.log('[Push] Welcome notification result:', pushResult, pushError);
        
        if (pushResult?.sent === 0) {
          console.warn('[Push] No subscriptions found for user');
        }
      } catch (e) {
        console.error('[Push] Welcome notification error:', e);
      }

      return true;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      toast({
        title: "Erro",
        description: "Não foi possível ativar as notificações",
        variant: "destructive",
      });
      setState(prev => ({ ...prev, loading: false }));
      return false;
    }
  }, [user, checkSupport]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!user) return false;

    setState(prev => ({ ...prev, loading: true }));

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remove from database
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint);

        // Unsubscribe from push manager
        await subscription.unsubscribe();
      }

      setState(prev => ({ ...prev, isSubscribed: false, loading: false }));
      
      toast({
        title: "Notificações Desativadas",
        description: "Você não receberá mais lembretes",
      });

      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      toast({
        title: "Erro",
        description: "Não foi possível desativar as notificações",
        variant: "destructive",
      });
      setState(prev => ({ ...prev, loading: false }));
      return false;
    }
  }, [user]);

  // Initialize state
  useEffect(() => {
    const isSupported = checkSupport();
    const isIOSPWA = checkIsIOSPWA();
    const permission = 'Notification' in window ? Notification.permission : 'default';
    
    setState(prev => ({
      ...prev,
      isSupported,
      isIOSPWA,
      permission,
    }));

    if (isSupported && user) {
      checkSubscription();
    } else {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [checkSupport, checkIsIOSPWA, checkSubscription, user]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    checkSubscription,
  };
}

// Helper function to convert base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length) as Uint8Array<ArrayBuffer>;

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
